import { useCallback, useEffect, useRef, useState } from "react";
import { getDraftMasterMenuPublishJob } from "@/api/admin.js";

const STORAGE_KEY = "masterMenuDraftPublishJobId";
const POLL_MS = 2000;

const TERMINAL_STATUSES = new Set(["completed", "partial", "failed"]);

export function isPublishJobTerminal(status) {
  return TERMINAL_STATUSES.has(String(status || "").trim());
}

export function usePublishJobTracker({ onPollTick }) {
  const [jobId, setJobId] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) || null
  );
  const [job, setJob] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [pollError, setPollError] = useState(null);
  const onPollTickRef = useRef(onPollTick);
  onPollTickRef.current = onPollTick;

  const startJob = useCallback((id, initialJob = null) => {
    const next = String(id || "").trim();
    if (!next) return;
    sessionStorage.setItem(STORAGE_KEY, next);
    setJobId(next);
    setJob(initialJob || null);
    setCollapsed(false);
    setPollError(null);
  }, []);

  const dismissJob = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setJobId(null);
    setJob(null);
    setCollapsed(false);
    setPollError(null);
  }, []);

  useEffect(() => {
    if (!jobId) return undefined;

    let cancelled = false;
    let intervalId = null;

    const poll = async () => {
      try {
        const res = await getDraftMasterMenuPublishJob(jobId);
        const data = res?.data ?? null;
        if (cancelled) return;

        setJob(data);
        setPollError(null);
        onPollTickRef.current?.(data);

        if (data?.status && isPublishJobTerminal(data.status)) {
          if (intervalId) clearInterval(intervalId);
        }
      } catch (e) {
        if (!cancelled) {
          setPollError(
            e?.response?.data?.message || e.message || "Could not load publish progress"
          );
        }
      }
    };

    poll();
    intervalId = setInterval(poll, POLL_MS);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [jobId]);

  const isActive =
    Boolean(jobId) &&
    (!job?.status || !isPublishJobTerminal(job.status));

  return {
    jobId,
    job,
    collapsed,
    setCollapsed,
    pollError,
    isActive,
    startJob,
    dismissJob,
  };
}
